<template>
  <div class="component-wrapper video d-flex">
    <v-progress-circular
      class="ma-auto"
      size="50"
      indeterminate
      color="primary"
      v-if="loader"
    ></v-progress-circular>

    <div v-if="!loadContentLoader" v-show="!loader" class="video-player-container">
      <vueVimeoPlayer
        ref="player"
        :video-url="openedContent.path"
        :controls="false"
        :options="{ 
          muted: !openedContent.hasSound,
          responsive: true,
          background: false
        }"
        @ready="onLoadVideo"
        @playing="onPlaying"
        @pause="onPause"
        @timeupdate="onTimeUpdate"
        @ended="onVideoEnded"
      />

      <div class="video-controls d-flex align-center pa-2">
        <v-btn icon @click="togglePlayPause">
          <v-icon>{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
        </v-btn>

        <input
          type="range"
          min="0"
          :max="duration"
          step="0.01"
          :value="currentTime"
          class="seek-bar mx-2"
          @input="handleSeek"
          @change="handleSeek"
        />

        <div class="time-display">{{ formatTime(currentTime) }} / {{ formatTime(duration) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { vueVimeoPlayer } from "vue-vimeo-player";
import { mapActions, mapMutations, mapState } from "vuex";

export default {
  components: { vueVimeoPlayer },
  data() {
    return {
      loadContentLoader: false,
      playerReady: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      seeking: false
    };
  },
  computed: {
    ...mapState({
      roomContent: (state) => state.guide.roomContent,
      remoteConnected: (state) => state.twilio.remoteConnected,
      openedContent: (state) => state.guide.openedContent,
    }),
    loader() {
      if (this.remoteConnected) {
        return (
          !this.roomContent.schoolVideoLoaded ||
          !this.roomContent.guideVideoLoaded
        );
      } else {
        return !this.roomContent.guideVideoLoaded;
      }
    },
  },
  methods: {
    ...mapMutations([
      "setOpenedContentFromPresentation",
      "setVideoPlayer",
      "setIsVideoPlaying",
    ]),
    ...mapActions(["loadContent", "loadVideo", "unloadContent"]),

    async onLoadVideo(player) {
      this.playerReady = true;
      this.vimeoPlayer = player;
      this.duration = await player.getDuration();
      
      if (!this.remoteConnected) {
        player.play();
      }
    },

    onPlaying() {
      this.isPlaying = true;
      this.setIsVideoPlaying(true);
      if (!this.roomContent.guideVideoLoaded) {
        this.vimeoPlayer.pause();
      }
    },

    async onPause() {
      this.isPlaying = false;
      this.setIsVideoPlaying(false);
      if (!this.roomContent.guideVideoLoaded) {
        this.setVideoPlayer(this.$refs.player);
        await this.loadVideo();
      }
    },

    onTimeUpdate(data) {
      if (!this.seeking) {
        this.currentTime = data.seconds;
      }
    },

    onVideoEnded() {
      this.isPlaying = false;
      this.currentTime = 0;
    },

    async handleSeek(e) {
      this.seeking = true;
      const seekTime = parseFloat(e.target.value);
      this.currentTime = seekTime;
      
      if (this.playerReady) {
        await this.vimeoPlayer.setCurrentTime(seekTime);
      }
      
      this.seeking = false;
    },

    togglePlayPause() {
      if (this.playerReady) {
        if (this.isPlaying) {
          this.vimeoPlayer.pause();
        } else {
          this.vimeoPlayer.play();
        }
      }
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }
  },
  async created() {
    this.loadContentLoader = true;
    await this.loadContent(this.openedContent.hasSound);
    this.loadContentLoader = false;
  },
  async beforeRouteUpdate(to, from, next) {
    this.loadContentLoader = true;
    this.setOpenedContentFromPresentation();
    this.setIsVideoPlaying(false);
    await this.loadContent(this.openedContent.hasSound);
    this.loadContentLoader = false;
    next();
  },
  async beforeRouteLeave(to, from, next) {
    if (!to.path.includes("/image/")) this.unloadContent();
    this.setIsVideoPlaying(false);
    next();
  },
};
</script>

<style lang="scss" scoped>
.component-wrapper.video {
  width: 100%;
  height: 100%;
  position: relative;
}

.video-player-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
}

.vue-vimeo-player {
  flex: 1;
  min-height: 0;
}

.video-controls {
  background-color: rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  display: flex;
  align-items: center;
  
  .seek-bar {
    flex: 1;
    margin: 0 12px;
    height: 6px;
    cursor: pointer;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #fff;
      cursor: pointer;
    }
  }
  
  .time-display {
    font-size: 14px;
    color: white;
    min-width: 100px;
    text-align: center;
  }
}
</style>